import React, { useMemo, useRef } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { TrendingAudioSuggestionsRow } from '../TrendingAudioSuggestions.types';
import { CellProps, Column } from 'react-table';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { formatDate } from '../../../../../../helpers';
import styles from './TrendingAudioSuggestions.module.css';
import cn from 'classnames';
import CheckIcon from '../../../../../../SharedComponents/svg/Icons/CheckIcon';
import CloseIcon from '../../../../../../SharedComponents/svg/Icons/CloseIcon';
import { SongListContextProvider } from '../../../../../Song/contexts/SongListContext';
import { Popover } from '@round/ui-kit';
import { ReactComponent as InfoCircleIcon } from '../../../../../../assets/InfoCircle.svg';
import Card from '../../../../../../ui/Card/Card';
import { useAudioListPlaybackHotkeys } from '../../../../../AudioPlayer/useAudioListPlaybackHotkeys';
import TiktokAudioBadge from '../../../../audios/components/TiktokAudioBadge/TiktokAudioBadge';

type TrendingAudioSuggestionsTableProps = Omit<PaginationTableProps<TrendingAudioSuggestionsRow>, 'columns'> & {
    onAcceptSuggestionClicked: (suggestion: TrendingAudioSuggestionsRow) => void;
    onRejectSuggestionClicked: (suggestion: TrendingAudioSuggestionsRow) => void;
};

const TrendingAudioSuggestionsTable = ({
    onAcceptSuggestionClicked,
    onRejectSuggestionClicked,
    ...props
}: TrendingAudioSuggestionsTableProps) => {
    const hotkeysInfoRef = useRef<SVGSVGElement | null>(null);
    const audioUrls = useMemo(() => props.data.map((row) => row.audioUrl), [props.data]);
    useAudioListPlaybackHotkeys(audioUrls);

    const columns: Column<TrendingAudioSuggestionsRow>[] = useMemo(
        () => [
            {
                Header: () => (
                    <div className={cn(styles.row, styles.audioNameHeader)}>
                        <span>Audio Name</span>
                        <InfoCircleIcon width={14} height={14} ref={hotkeysInfoRef} />
                        <Popover anchorElement={hotkeysInfoRef.current} showOn="hover">
                            <Card className={styles.hotkeysContainer}>
                                <div>
                                    <kbd>a</kbd> - play previous audio
                                </div>
                                <div>
                                    <kbd>s</kbd> - play/pause
                                </div>
                                <div>
                                    <kbd>d</kbd> - play next audio
                                </div>
                            </Card>
                        </Popover>
                    </div>
                ),
                accessor: 'audio_title',
                Cell: ({ row: { original } }) => {
                    return (
                        <TiktokAudioBadge
                            loading={props.loading}
                            audio={{
                                tiktokId: original.audio_tiktok_id,
                                isOriginal: original.audio_is_original,
                                audioPlayUrl: original.audioUrl,
                                title: original.audio_title,
                                imageUrl: original.imageUrl,
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Author Name',
                accessor: 'audio_author_name',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Date Detected',
                accessor: 'created',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{formatDate(value)}</>;
                },
            },
            {
                Header: 'Actions',
                id: 'actions',
                Cell: ({ row: { original } }: CellProps<TrendingAudioSuggestionsRow>) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.actions}>
                            <button
                                className={styles.actionButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAcceptSuggestionClicked(original);
                                }}
                            >
                                <CheckIcon
                                    className={cn(styles.actionButtonIcon, styles.acceptButton)}
                                    width={18}
                                    height={18}
                                />
                                Add
                            </button>

                            <button
                                className={styles.actionButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRejectSuggestionClicked(original);
                                }}
                            >
                                <CloseIcon
                                    className={cn(styles.actionButtonIcon, styles.rejectButton)}
                                    width={11}
                                    height={11}
                                />
                                Reject
                            </button>
                        </div>
                    );
                },
            },
        ],
        [onAcceptSuggestionClicked, onRejectSuggestionClicked, props.loading]
    );

    return (
        <SongListContextProvider>
            <PaginationTable columns={columns} {...props} />
        </SongListContextProvider>
    );
};

export default TrendingAudioSuggestionsTable;
